/**
 * Resource Hub - Frontend JS Controller
 * Handles native client-side filtering, sorting, and pagination.
 */
class ResourceHub {
  constructor(container) {
    this.container = container;
    this.grid = container.querySelector('#rh_articles-grid');
    if (!this.grid) return;

    this.cards = Array.from(this.grid.querySelectorAll('.rh_card'));
    this.loadMoreBtn = container.querySelector('#rh_load-more-btn');
    this.emptyState = container.querySelector('.rh_empty-state');
    
    // Filters and Inputs
    this.filterButtons = container.querySelectorAll('.rh_filter-btn');
    this.tagFilterSelect = container.querySelector('#rh_tag-filter-select');
    this.blogTypeSelect = container.querySelector('#rh_blog-type-select');
    
    // Load config from container data attributes
    this.itemsPerPage = parseInt(container.dataset.itemsPerPage) || 9;
    
    // Initial State
    this.state = {
      activeTag: 'all',
      activeBlog: 'all',
      visibleCount: this.itemsPerPage
    };
    
    this.init();
  }
  
  init() {
    // 1. Tag Filters
    this.filterButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Remove active class from other buttons
        this.filterButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        
        // Update active tag in state and reset page limit
        this.state.activeTag = btn.dataset.filterTag || 'all';
        this.state.visibleCount = this.itemsPerPage;
        
        this.update();
      });
    });
    
    // 2. Blog Type Dropdown Filter
    if (this.blogTypeSelect) {
      this.blogTypeSelect.addEventListener('change', (e) => {
        this.state.activeBlog = e.target.value || 'all';
        this.state.visibleCount = this.itemsPerPage;
        
        this.update();
      });
    }

    // 3. Tag Filter Select Dropdown (mobile/tablet)
    if (this.tagFilterSelect) {
      this.tagFilterSelect.addEventListener('change', (e) => {
        this.state.activeTag = e.target.value || 'all';
        this.state.visibleCount = this.itemsPerPage;
        
        this.update();
      });
    }
    
    // 4. Load More Button
    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Increase visible items count
        this.state.visibleCount += this.itemsPerPage;
        
        this.update();
      });
    }
    
    // Initial Render
    this.update();
  }
  
  update() {
    // 1. Get cards that pass our filters
    const filteredCards = this.applyFilters();
    
    // 2. Update visibility and paginate
    this.applyPagination(filteredCards);
    
    // 3. Toggle empty state indicator
    if (filteredCards.length === 0) {
      if (this.emptyState) this.emptyState.style.display = 'block';
    } else {
      if (this.emptyState) this.emptyState.style.display = 'none';
    }
  }
  
  applyFilters() {
    return this.cards.filter(card => {
      const cardBlog = card.dataset.blog || '';
      
      // Split tags by comma, clean and downcase
      const cardTagsStr = card.dataset.tags || '';
      const cardTags = cardTagsStr ? cardTagsStr.split(',').map(t => t.trim().toLowerCase()) : [];
      
      // Match blog type
      const matchesBlog = this.state.activeBlog === 'all' || cardBlog === this.state.activeBlog;
      
      // Match tag
      const matchesTag = this.state.activeTag === 'all' || cardTags.includes(this.state.activeTag.toLowerCase());
      
      return matchesBlog && matchesTag;
    });
  }
  
  applyPagination(filteredAndSortedCards) {
    // Hide all cards first
    this.cards.forEach(card => {
      card.style.display = 'none';
      card.classList.remove('rh_card--visible');
    });
    
    // Slice only the ones we need to show
    const toShow = filteredAndSortedCards.slice(0, this.state.visibleCount);
    
    // Re-append nodes in the sorted order so layout matches the sort selection
    toShow.forEach(card => {
      this.grid.appendChild(card);
      card.style.display = 'block'; // Make it part of layout
      
      // Delay applying the opacity class slightly so the transition triggers
      requestAnimationFrame(() => {
        card.classList.add('rh_card--visible');
      });
    });
    
    // Manage Load More button visibility
    if (this.loadMoreBtn) {
      if (filteredAndSortedCards.length > this.state.visibleCount) {
        this.loadMoreBtn.style.display = 'inline-flex';
      } else {
        this.loadMoreBtn.style.display = 'none';
      }
    }
  }
}

// Initialize on DOM Ready
document.addEventListener('DOMContentLoaded', () => {
  const hubs = document.querySelectorAll('.rh_wrapper');
  hubs.forEach(hub => new ResourceHub(hub));
});

// Support Shopify Section re-renders in the Customizer Theme Editor
document.addEventListener('shopify:section:load', (e) => {
  const hub = e.target.querySelector('.rh_wrapper');
  if (hub) {
    new ResourceHub(hub);
  }
});
